import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Container,
    Avatar,
    Chip,
    useTheme
} from '@mui/material';
import { ThemeToggle } from '@components/common/ThemeToggle/ThemeToggle';
import { useAuth } from '@store/authStore';
import {
    AdminPanelSettings
} from '@mui/icons-material';

const RoleSelection: React.FC = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const theme = useTheme();

    console.log('🎯 RoleSelection: User data from useAuth:', user);
    console.log('💾 RoleSelection: localStorage user:', localStorage.getItem('user'));
    console.log('👤 RoleSelection: User name to display:', user?.name || 'User');

    const roles = [
        {
            id: 'admin',
            title: 'Admin',
            description: 'Administrative control and user management',
            icon: <AdminPanelSettings />,
            color: '#2196f3',
            gradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
            features: ['User Management', 'Dashboard', 'Reports'],
            path: '/dashboard'
        }
    ];

    const handleRoleSelect = (rolePath: string) => {
        console.log('Selected role path:', rolePath);
        navigate(rolePath);
    };

    return (
        <>
            <ThemeToggle />
            <Box sx={{
                minHeight: '100vh',
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #1a1f3a 0%, #20232a 100%)'
                    : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                display: 'flex',
                alignItems: 'center',
                py: 4
            }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={6}>
                        <Box sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 3,
                            overflow: 'hidden'
                        }}>
                            <img
                                src="/assets/logos/logo-192.png"
                                alt="Nucleus Logo"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </Box>
                        <Typography variant="h3" sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 700,
                            mb: 2
                        }}>
                            Welcome to Nucleus
                        </Typography>
                        <Typography variant="h6" sx={{
                            color: theme.palette.text.secondary,
                            mb: 1
                        }}>
                            Hello, {user?.name || 'User'}! 👋
                        </Typography>
                        <Typography variant="body1" sx={{
                            color: theme.palette.text.secondary,
                            maxWidth: 600,
                            mx: 'auto'
                        }}>
                            Choose your dashboard to access the features and tools you need for your role.
                        </Typography>
                    </Box>

                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        gap: 4,
                        maxWidth: 1200,
                        mx: 'auto'
                    }}>
                        {roles.map((role) => (
                            <Box key={role.id}>
                                <Card sx={{
                                    height: '100%',
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                                        border: `1px solid ${role.color}40`,
                                    }
                                }}
                                    onClick={() => handleRoleSelect(role.path)}
                                >
                                    <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Box textAlign="center" mb={3}>
                                            <Avatar sx={{
                                                width: 80,
                                                height: 80,
                                                background: role.gradient,
                                                mx: 'auto',
                                                mb: 2,
                                                boxShadow: `0 8px 32px ${role.color}40`
                                            }}>
                                                {role.icon}
                                            </Avatar>
                                            <Typography variant="h5" sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: 600,
                                                mb: 1
                                            }}>
                                                {role.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{
                                                color: theme.palette.text.secondary,
                                                mb: 3
                                            }}>
                                                {role.description}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle2" sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: 600,
                                                mb: 2
                                            }}>
                                                Key Features:
                                            </Typography>
                                            {role.features.map((feature, index) => (
                                                <Chip
                                                    key={index}
                                                    label={feature}
                                                    size="small"
                                                    sx={{
                                                        background: `${role.color}20`,
                                                        color: role.color,
                                                        border: `1px solid ${role.color}40`,
                                                        mr: 1,
                                                        mb: 1,
                                                        fontSize: '0.75rem'
                                                    }}
                                                />
                                            ))}
                                        </Box>

                                        <Button
                                            variant="contained"
                                            fullWidth
                                            sx={{
                                                background: role.gradient,
                                                borderRadius: 2,
                                                py: 1.5,
                                                mt: 3,
                                                textTransform: 'none',
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                boxShadow: `0 4px 16px ${role.color}40`,
                                                '&:hover': {
                                                    background: role.gradient,
                                                    boxShadow: `0 8px 24px ${role.color}60`,
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                            onClick={() => handleRoleSelect(role.path)}
                                        >
                                            Access {role.title} Dashboard
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Box>
                        ))}
                    </Box>

                    <Box textAlign="center" mt={6}>
                        <Typography variant="body2" sx={{
                            color: theme.palette.text.secondary,
                            mb: 2
                        }}>
                            You can switch between dashboards anytime from the navigation menu.
                        </Typography>
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: '#2196F3', // Solid blue background like in image
                                color: 'white', // White text for high contrast
                                border: 'none',
                                '&:hover': {
                                    backgroundColor: '#1976D2', // Darker blue on hover
                                    transform: 'none',
                                    boxShadow: 'none'
                                },
                                '&:focus': {
                                    outline: 'none',
                                    boxShadow: 'none'
                                },
                                '&:active': {
                                    backgroundColor: '#1565C0', // Even darker blue on active
                                    transform: 'none'
                                }
                            }}
                            onClick={() => {
                                logout(); // Logout user first
                                navigate('/login');
                            }}
                        >
                            Back to Login
                        </Button>
                    </Box>
                </Container>
            </Box>
        </>
    );
};

export default RoleSelection;
